import { z } from "zod";

const nonNegativeVersion = z.number().int().min(0).max(2147483647);
const positiveRevision = z.number().int().min(1).max(2147483647);
const timestamp = z.string().min(1);

/**
 * Application-user session controls. A value of 0 means "unlimited" for the
 * session cap. These controls apply to newly-created application-user
 * sessions only; they never change administrator CloudBase login behaviour.
 */
export const loginPolicyValuesSchema = z.object({
  sessionTtlSeconds: z.number().int().min(300).max(31_536_000),
  maxActiveSessionsPerUser: z.number().int().min(0).max(100),
  successfulLoginRateLimit: z.object({
    enabled: z.boolean(),
    windowSeconds: z.number().int().min(60).max(86_400),
    maxSuccessfulLogins: z.number().int().min(1).max(1_000),
  }).strict(),
}).strict();
export type LoginPolicyValues = z.infer<typeof loginPolicyValuesSchema>;

export const defaultLoginPolicyValues: LoginPolicyValues = {
  sessionTtlSeconds: 2_592_000,
  maxActiveSessionsPerUser: 0,
  successfulLoginRateLimit: {
    enabled: false,
    windowSeconds: 3_600,
    maxSuccessfulLogins: 30,
  },
};

export const loginPolicyAppIdSchema = z.string().trim().min(1).max(128);

export const loginPolicyRevisionSchema = z.object({
  revision: positiveRevision,
  values: loginPolicyValuesSchema,
  publishedAt: timestamp,
  publishedBy: z.string().min(1),
  action: z.enum(["publish", "rollback"]),
  sourceRevision: positiveRevision.nullable(),
}).strict();
export type LoginPolicyRevision = z.infer<typeof loginPolicyRevisionSchema>;

export const loginPolicySchema = z.object({
  appId: loginPolicyAppIdSchema,
  appName: z.string(),
  appStatus: z.enum(["active", "disabled"]),
  version: nonNegativeVersion,
  draft: loginPolicyValuesSchema,
  published: loginPolicyRevisionSchema.nullable(),
  createdAt: timestamp,
  updatedAt: timestamp,
  updatedBy: z.string(),
}).strict();
export type LoginPolicy = z.infer<typeof loginPolicySchema>;

export const loginPolicyListQuerySchema = z.object({
  keyword: z.string().trim().max(80).default(""),
  appStatus: z.enum(["all", "active", "disabled"]).default("all"),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export type LoginPolicyListQuery = z.infer<typeof loginPolicyListQuerySchema>;

export const loginPolicyPageSchema = z.object({
  items: z.array(loginPolicySchema),
  total: z.number().int().min(0),
}).strict();
export type LoginPolicyPage = z.infer<typeof loginPolicyPageSchema>;

export const loginPolicyPaginationSchema = z.object({
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export type LoginPolicyPagination = z.infer<typeof loginPolicyPaginationSchema>;

export const loginPolicyUpdateSchema = z.object({
  values: loginPolicyValuesSchema,
  expectedVersion: nonNegativeVersion,
}).strict();
export type LoginPolicyUpdateInput = z.infer<typeof loginPolicyUpdateSchema>;

export const loginPolicyPublishSchema = z.object({
  expectedVersion: nonNegativeVersion,
  mutationId: z.uuid(),
}).strict();
export type LoginPolicyPublishInput = z.infer<typeof loginPolicyPublishSchema>;

export const loginPolicyRollbackSchema = loginPolicyPublishSchema.extend({
  targetRevision: positiveRevision,
}).strict();
export type LoginPolicyRollbackInput = z.infer<typeof loginPolicyRollbackSchema>;

export const loginPolicyRevisionsPageSchema = z.object({
  items: z.array(loginPolicyRevisionSchema),
  total: z.number().int().min(0),
}).strict();
export type LoginPolicyRevisionsPage = z.infer<typeof loginPolicyRevisionsPageSchema>;

export const loginPolicyAuditEntrySchema = z.object({
  id: z.string(),
  actorId: z.string(),
  action: z.enum(["update", "publish", "rollback"]),
  appId: loginPolicyAppIdSchema,
  version: nonNegativeVersion,
  revision: positiveRevision.nullable(),
  requestId: z.string().nullable(),
  createdAt: timestamp,
}).strict();

export const loginPolicyAuditPageSchema = z.object({
  items: z.array(loginPolicyAuditEntrySchema),
  total: z.number().int().min(0),
}).strict();
export type LoginPolicyAuditPage = z.infer<typeof loginPolicyAuditPageSchema>;
